import { BulletListContent, BulletListIcon, getTokenCssVar, Li, Link, Typography } from '@vp/swan'
import type { AlternateLanguage, Country } from '../../../types/country'
import { getHeaderTracker } from '../../../tracking'
import { resolveUrlForTenant } from '../../../utils/page-availability'
import { resolveDeepLinkUrl } from '../../../utils/deep-link'
import { setSelectedCountry } from '../../../utils/country'

interface CountryListItemProps {
  country: Country
  isSelected: boolean
  locale?: string
  tenant?: string
}

export const CountryListItem: React.FC<CountryListItemProps> = ({ country, isSelected, locale, tenant }) => {
  const countryIconBorder = `1px solid ${getTokenCssVar('SwanSemColorBorderStandard')}`

  const handleCountryClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    getHeaderTracker().trackFlyOutClick('Country Selector', `select_${country.text}`)
    setSelectedCountry(country.cloudflareCountryCode)

    const deepUrl = await resolveDeepLinkUrl(country.url, country.locale, locale ?? '', tenant)
    globalThis.location.href = deepUrl
  }

  return (
    <Li key={country.text} fontSkin={isSelected ? 'body-small-bold' : 'body-small'} p={0} selected={isSelected} aria-current={isSelected ? 'true' : undefined}>
      <BulletListIcon src={country.imageUrl.image.url} width={24} height={24} loading="lazy" style={{ border: countryIconBorder }} alt="" />
      <BulletListContent style={{ minWidth: 'fit-content' }}>
        <CountryName country={country} isSelected={isSelected} tenant={tenant} onCountryClick={handleCountryClick} />
        {country.alternateLanguages.length > 0 && <AlternateLanguageLinks alternateLanguages={country.alternateLanguages} locale={locale} tenant={tenant} />}
      </BulletListContent>
    </Li>
  )
}

interface CountryNameProps {
  country: Country
  isSelected: boolean
  tenant?: string
  onCountryClick: (e: React.MouseEvent) => void
}

const CountryName = ({ country, isSelected, tenant, onCountryClick }: CountryNameProps) => {
  if (isSelected) {
    return country.text
  }

  return (
    <Link href={resolveUrlForTenant(country.url, tenant)} skin="unstyled" onClick={onCountryClick}>
      {country.text}
    </Link>
  )
}

type AlternateLanguageLinksProps = {
  alternateLanguages: AlternateLanguage[]
  locale?: string
  tenant?: string
}

const AlternateLanguageLinks = ({ alternateLanguages, locale, tenant }: AlternateLanguageLinksProps) => {
  const handleAlternateLanguageClick = async (e: React.MouseEvent, alternateLanguage: AlternateLanguage) => {
    e.preventDefault()
    getHeaderTracker().trackFlyOutClick('Country Selector', `select_language_${alternateLanguage.text}`)

    const deepUrl = await resolveDeepLinkUrl(alternateLanguage.url, alternateLanguage.locale, locale ?? '', tenant)
    globalThis.location.href = deepUrl
  }

  return (
    <>
      {alternateLanguages.map((alternateLanguage) =>
        alternateLanguage.locale.toLowerCase() === locale?.toLowerCase() ? (
          <Typography key={alternateLanguage.locale} ml={2} display="inline-block">
            {alternateLanguage.text}
          </Typography>
        ) : (
          <Link
            key={alternateLanguage.locale}
            href={resolveUrlForTenant(alternateLanguage.url, tenant)}
            skin="unstyled"
            ml={2}
            onClick={(e: React.MouseEvent) => handleAlternateLanguageClick(e, alternateLanguage)}
          >
            {alternateLanguage.text}
          </Link>
        ),
      )}
    </>
  )
}
